import {useState, useCallback, useEffect, useRef} from 'react';

interface CropArea {
    x: number;
    y: number;
    width: number;
    height: number;
}

interface UseImageCropperProps {
    imageUrl: string;
    aspect?: number;
}

export const useImageCropper = ({imageUrl, aspect = 1}: UseImageCropperProps) => {
    const [imageNaturalSize, setImageNaturalSize] = useState({width: 0, height: 0});
    const [containerSize, setContainerSize] = useState({width: 0, height: 0});
    const [imageBaseSize, setImageBaseSize] = useState({width: 0, height: 0});
    const [zoom, setZoom] = useState(1);
    const [cropArea, setCropArea] = useState<CropArea>({x: 0, y: 0, width: 0, height: 0});
    const [imageOffset, setImageOffset] = useState({x: 0, y: 0});
    const [isImageLoaded, setIsImageLoaded] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!imageUrl) return;

        const img = new Image();
        img.onload = () => {
            const naturalWidth = img.naturalWidth;
            const naturalHeight = img.naturalHeight;

            const maxWidth = containerRef.current?.clientWidth || 400;
            const maxHeight = window.innerHeight * 0.6 || 400;

            let containerW = maxWidth;
            let containerH = containerW / aspect;

            if (containerH > maxHeight) {
                containerH = maxHeight;
                containerW = containerH * aspect;
            }

            const imageAspect = naturalWidth / naturalHeight;
            const containerAspect = containerW / containerH;

            // baseSize jako cover: większy wymiar wypełnia kontener
            let baseW, baseH;
            if (imageAspect > containerAspect) {
                baseH = containerH;
                baseW = containerH * imageAspect;
            } else {
                baseW = containerW;
                baseH = containerW / imageAspect;
            }

            const cropWidth = Math.min(baseW * 0.8, baseH * 0.8 * aspect, containerW * 0.9);
            const cropHeight = cropWidth / aspect;

            const initCropX = (containerW - cropWidth) / 2;
            const initCropY = (containerH - cropHeight) / 2;

            setImageNaturalSize({width: naturalWidth, height: naturalHeight});
            setContainerSize({width: containerW, height: containerH});
            setImageBaseSize({width: baseW, height: baseH});

            setCropArea({
                x: initCropX,
                y: initCropY,
                width: cropWidth,
                height: cropHeight,
            });

            setImageOffset({
                x: (containerW - baseW) / 2,
                y: (containerH - baseH) / 2,
            });

            setZoom(1);
            setIsImageLoaded(true);
        };
        img.src = imageUrl;
    }, [imageUrl, aspect]);

    const clampCropArea = useCallback((crop: CropArea, imgX: number, imgY: number, imgW: number, imgH: number): CropArea => {
        let {x, y, width, height} = crop;

        const maxW = Math.min(containerSize.width, imgW);
        const maxH = Math.min(containerSize.height, imgH);

        if (width > maxW) {
            width = maxW;
            height = width / aspect;
        }
        if (height > maxH) {
            height = maxH;
            width = height * aspect;
        }

        // Granice cropArea = przecięcie kontenera i obszaru zdjęcia
        const minX = Math.max(0, imgX);
        const maxXBoundary = Math.min(containerSize.width, imgX + imgW) - width;
        const minY = Math.max(0, imgY);
        const maxYBoundary = Math.min(containerSize.height, imgY + imgH) - height;

        if (x < minX) x = minX;
        if (x > maxXBoundary) x = maxXBoundary;
        if (y < minY) y = minY;
        if (y > maxYBoundary) y = maxYBoundary;

        return {x, y, width, height};
    }, [containerSize, aspect]);

    const handleZoom = useCallback((delta: number) => {
        setZoom(prevZoom => {
            if (!containerSize.width || !imageBaseSize.width) return prevZoom;

            const minZoom = Math.min(
                containerSize.width / imageBaseSize.width,
                containerSize.height / imageBaseSize.height
            );

            const newZoom = Math.min(3, Math.max(minZoom, prevZoom + delta));
            if (newZoom === prevZoom) return prevZoom;

            const newImageW = imageBaseSize.width * newZoom;
            const newImageH = imageBaseSize.height * newZoom;
            const scaleRatio = newZoom / prevZoom;

            // Punkt fokalny: środek cropArea (to, co użytkownik kadruje)
            const cx = cropArea.x + cropArea.width / 2;
            const cy = cropArea.y + cropArea.height / 2;

            setImageOffset(prevOffset => {
                let newX = cx - (cx - prevOffset.x) * scaleRatio;
                let newY = cy - (cy - prevOffset.y) * scaleRatio;

                // Per oś: mniejsze niż kontener → wycentruj; większe → nie odsłaniaj tła
                if (newImageW <= containerSize.width) {
                    newX = (containerSize.width - newImageW) / 2;
                } else {
                    const minX = containerSize.width - newImageW;
                    const maxX = 0;
                    if (newX < minX) newX = minX;
                    if (newX > maxX) newX = maxX;
                }

                if (newImageH <= containerSize.height) {
                    newY = (containerSize.height - newImageH) / 2;
                } else {
                    const minY = containerSize.height - newImageH;
                    const maxY = 0;
                    if (newY < minY) newY = minY;
                    if (newY > maxY) newY = maxY;
                }

                return {x: newX, y: newY};
            });

            return newZoom;
        });
    }, [imageBaseSize, containerSize, cropArea]);

    const moveCrop = useCallback((dx: number, dy: number) => {
        const imgW = imageBaseSize.width * zoom;
        const imgH = imageBaseSize.height * zoom;

        setCropArea(prev => {
            const newCrop = {
                ...prev,
                x: prev.x + dx,
                y: prev.y + dy,
            };
            return clampCropArea(newCrop, imageOffset.x, imageOffset.y, imgW, imgH);
        });
    }, [imageBaseSize, zoom, imageOffset, clampCropArea]);

    const resizeCrop = useCallback((corner: string, dx: number, dy: number) => {
        const imgW = imageBaseSize.width * zoom;
        const imgH = imageBaseSize.height * zoom;
        const minSize = 50;

        setCropArea(prev => {
            let newCrop = {...prev};

            switch (corner) {
                case 'se':
                    newCrop.width = Math.max(minSize, prev.width + dx);
                    newCrop.height = newCrop.width / aspect;
                    break;
                case 'sw':
                    newCrop.width = Math.max(minSize, prev.width - dx);
                    newCrop.x = prev.x + prev.width - newCrop.width;
                    newCrop.height = newCrop.width / aspect;
                    break;
                case 'ne':
                    newCrop.width = Math.max(minSize, prev.width + dx);
                    newCrop.height = newCrop.width / aspect;
                    newCrop.y = prev.y + prev.height - newCrop.height;
                    break;
                case 'nw':
                    newCrop.width = Math.max(minSize, prev.width - dx);
                    newCrop.x = prev.x + prev.width - newCrop.width;
                    newCrop.height = newCrop.width / aspect;
                    newCrop.y = prev.y + prev.height - newCrop.height;
                    break;
            }

            return clampCropArea(newCrop, imageOffset.x, imageOffset.y, imgW, imgH);
        });
    }, [imageBaseSize, zoom, aspect, imageOffset, clampCropArea]);

    // Po każdej zmianie zoomu/offsetu — wymuś, by cropArea została w obrębie zdjęcia
    useEffect(() => {
        if (!isImageLoaded) return;
        const imgW = imageBaseSize.width * zoom;
        const imgH = imageBaseSize.height * zoom;
        setCropArea(prev => clampCropArea(prev, imageOffset.x, imageOffset.y, imgW, imgH));
    }, [zoom, imageOffset, imageBaseSize, isImageLoaded, clampCropArea]);

    const getCroppedBlob = useCallback((): Promise<Blob> => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = cropArea.width;
                canvas.height = cropArea.height;

                const ctx = canvas.getContext('2d');
                if (!ctx) return reject(new Error('No context'));

                const scale = imageNaturalSize.width / (imageBaseSize.width * zoom);

                const relativeX = cropArea.x - imageOffset.x;
                const relativeY = cropArea.y - imageOffset.y;

                ctx.drawImage(
                    img,
                    relativeX * scale,
                    relativeY * scale,
                    cropArea.width * scale,
                    cropArea.height * scale,
                    0, 0,
                    cropArea.width,
                    cropArea.height
                );

                canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('No blob')), 'image/jpeg', 0.95);
            };
            img.onerror = () => reject(new Error('Image load failed'));
            img.src = imageUrl;
        });
    }, [imageUrl, imageNaturalSize, imageBaseSize, cropArea, zoom, imageOffset]);

    return {
        isImageLoaded,
        containerSize,
        displayWidth: imageBaseSize.width * zoom,
        displayHeight: imageBaseSize.height * zoom,
        imageOffset,
        cropArea,
        zoom,
        containerRef,
        handleZoom,
        moveCrop,
        resizeCrop,
        getCroppedBlob,
    };
};
